import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
} from "recharts";
import { Activity, Eye, Users, Radio } from "lucide-react";

const RANGES = [
  { id: "1h", label: "Last 1h" },
  { id: "24h", label: "Last 24h" },
  { id: "7d", label: "Last 7d" },
];

function Metric({ icon: Icon, label, value, accent, live }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`traffic-metric-${label}`}>
      <div className="flex items-center justify-between">
        <span className="label-mono text-slate-400">{label}</span>
        <Icon size={16} style={{ color: accent }} />
      </div>
      <div className="mt-3 font-display font-extrabold text-3xl text-ink flex items-center gap-2">
        {value}
        {live && Number(value) > 0 && <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />}
      </div>
    </div>
  );
}

function Bars({ title, data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`traffic-breakdown-${title}`}>
      <div className="label-mono text-slate-400 mb-4">{title}</div>
      {data.length === 0 ? (
        <div className="text-sm text-slate-400">No data yet</div>
      ) : (
        <div className="space-y-3">
          {data.map((d) => (
            <div key={d.name}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">{d.name}</span>
                <span className="text-ink font-medium">{d.count}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-brand rounded-full" style={{ width: `${(d.count / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrafficPanel() {
  const [range, setRange] = useState("24h");
  const [data, setData] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async (r) => {
    try {
      const { data } = await api.get("/admin/traffic", { params: { range: r } });
      setData(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail, "Could not load traffic data")); }
  }, []);

  useEffect(() => {
    load(range);
    timer.current = setInterval(() => load(range), 5000);
    return () => clearInterval(timer.current);
  }, [range, load]);

  return (
    <div className="space-y-5" data-testid="traffic-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Radio size={15} className="text-emerald-500" /> Live — auto-refreshing every 5s
        </div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-full p-1">
          {RANGES.map((r) => (
            <button
              key={r.id} onClick={() => setRange(r.id)} data-testid={`traffic-range-${r.id}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${range === r.id ? "bg-ink text-white" : "text-slate-600 hover:text-ink"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Metric icon={Radio} label="Online now" value={data?.online_now ?? "—"} accent="#059669" live />
        <Metric icon={Eye} label="Page views" value={data?.total_views ?? "—"} accent="#2563EB" />
        <Metric icon={Users} label="Unique sessions" value={data?.unique_sessions ?? "—"} accent="#D97706" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="traffic-chart">
        <div className="label-mono text-slate-400 mb-4 flex items-center gap-1.5"><Activity size={14} /> Page views over time</div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={data?.series || []} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Area type="monotone" dataKey="views" stroke="#2563EB" strokeWidth={2} fill="url(#pv)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="traffic-top-pages">
          <div className="label-mono text-slate-400 mb-4">Most-visited pages</div>
          {(!data || data.top_pages.length === 0) ? (
            <div className="text-sm text-slate-400">No page views yet</div>
          ) : (
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={data.top_pages} layout="vertical" margin={{ top: 0, right: 12, left: 30, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: "#334155" }} width={92} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="views" fill="#2563EB" radius={[0, 6, 6, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="space-y-4">
          <Bars title="Devices" data={data?.devices || []} />
          <Bars title="Browsers" data={data?.browsers || []} />
        </div>
      </div>
    </div>
  );
}
